<template>
  <div class="w-full flex items-center justify-center">
    <!-- right side -->
    <div
      class="flex flex-col bg-white w-full md:w-[50%] xl:w-[38%] p-6 items-center space-y-2 border border-slate-200 shadow-lg rounded-lg h-auto"
    >
      <div
        v-if="!resetOtpSent"
        class="w-full flex flex-col items-center space-y-5"
      >
        <p class="text-subDark font-bold text-base md:text-2xl">
          {{ $t("reset-your-password") }}
        </p>
        <p class="text-slate-500 font-medium text-xs md:text-sm text-center">
          {{
            $t("Forgot_your_password_No_worries")
          }}
        </p>
        <div class="relative w-full flex flex-col space-y-1">
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("Email") }}
          </p>
          <input
            v-model="email"
            type="text"
            :class="{ 'pr-10': locale === 'ar' }"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('Email')"
            required
          />
        </div>
        <button
          @click="requestOtp"
          class="bg-subDark text-sm md:text-base text-white font-semibold py-1 px-12 rounded-lg w-full h-10"
        >
          <p class="pl-4">
            {{ $t("Request_reset_code") }}
          </p>
        </button>

        <p class="text-sm md:text-base font-medium text-slate-600">
          {{ $t("Dont_have_an_account_yet") }}
          <a class="text-[#4867C8]" href="/signing-up">{{ $t("Sign_up") }}</a>
        </p>
      </div>
      <div
        v-if="resetOtpSent && !otpCorrect"
        class="w-full flex flex-col justify-center max-h-full items-center space-y-5"
      >
        <div
          class="flex flex-col items-center justify-center text-center space-y-2"
        >
          <div class="flex flex-row text-sm md:text-lg font-medium text-slate-600">
            <p>
              {{ $t("We_have_sent_a_code_to_your_email") }}
              {{ userHiddenMail() }}
            </p>
          </div>
        </div>

        <div
          id="otp"
          class="flex flex-row items-center justify-between mx-auto w-full max-w-l space-x-2"
        >
          <div class="w-16 h-16">
            <input
              class="otp-input w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-slate-600 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
              type="text"
              maxlength="1"
              id="first"
              v-model="otpCode[0]"
            />
          </div>
          <div class="w-16 h-16">
            <input
              class="otp-input w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-slate-600 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
              type="text"
              maxlength="1"
              id="second"
              v-model="otpCode[1]"
            />
          </div>
          <div class="w-16 h-16">
            <input
              class="otp-input w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-slate-600 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
              type="text"
              maxlength="1"
              id="third"
              v-model="otpCode[2]"
            />
          </div>
          <div class="w-16 h-16">
            <input
              class="otp-input w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-slate-600 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
              type="text"
              maxlength="1"
              id="fourth"
              v-model="otpCode[3]"
            />
          </div>
          <div class="w-16 h-16">
            <input
              class="otp-input w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-slate-600 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
              type="text"
              maxlength="1"
              id="fifth"
              v-model="otpCode[4]"
            />
          </div>
          <div class="w-16 h-16">
            <input
              class="otp-input w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-slate-600 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
              type="text"
              maxlength="1"
              id="sixth"
              v-model="otpCode[5]"
            />
          </div>
        </div>

        <button
          @click="verifyOtp"
          class="bg-subDark text-white font-semibold py-1 px-12 rounded-lg w-full h-10"
        >
          <svg
            aria-hidden="true"
            :class="{ hidden: !isLoading }"
            class="h-6 text-gray-200 animate-spin fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <p class="pl-4">{{ $t("Verify_code") }}</p>
        </button>

        <div
          v-if="!resendDisabled"
          class="flex flex-row items-center justify-center text-center text-sm font-medium space-x-1 text-gray-500"
        >
          <p class="text-sm md:text-base font-medium text-slate-600">
            {{ $t("Didn_recieve_code") }}
          </p>
          <a
            class="flex flex-row font-bold text-sm md:text-lg items-center text-[#061B44]"
            href="#"
            role="button"
            @click.prevent="requestOtp"
            >{{ $t("Resend") }}</a
          >
        </div>
        <div v-else>
          <p class="text-base font-medium text-slate-600">
            {{ $t("Resend_code_in_seconds") }} {{ countdown.toFixed(0) }}
            {{ $t("seconds") }}
          </p>
        </div>
        <p v-if="serverError" class="text-sm font-medium text-red-500">
          {{ $t('were-fixing-a-technical-issue-so-please-try') }}
        </p>
        <div class="h-10"></div>
      </div>
      <div
        v-if="resetToken.length != 0 && otpCorrect && !passReseted"
        class="w-full flex flex-col max-h-full items-center space-y-5"
      >
        <div class="relative w-full flex flex-col space-y-1">
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("Password") }}
          </p>
          <input
            v-model="password"
            :type="viewPassword ? 'text' : 'password'"
            :class="{ 'pr-10': locale === 'ar' }"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('Password')"
            required
          />
        </div>
        <div class="relative w-full flex flex-col space-y-1">
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("Password") }}
          </p>
          <input
            v-model="confirmpassword"
            :type="viewPassword ? 'text' : 'password'"
            :class="{ 'pr-10': locale === 'ar' }"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('Confirm_password')"
            required
          />
        </div>
        <button
          @click="resetPassword"
          class="bg-subDark text-white font-semibold py-1 px-12 rounded-lg w-full h-10"
        >
          <p class="pl-4">{{ $t("Reset_password") }}</p>
        </button>
        <p v-if="passwordShort" class="text-red-600">
          {{ $t("Password_is_too_short") }}
        </p>
      </div>
      <div
        v-if="passReseted"
        class="w-full flex flex-col max-h-full items-center space-y-5"
      >
        <p class="text-sm md:text-base text-center font-medium text-slate-800">
          {{
            $t(
              "Reset_password_complete_successfully_now_you_can_login_to_your_account"
            )
          }}
        </p>
      </div>
    </div>
    <!-- left side -->
    <!--  <div
      class="flex flex-col bg-white lg:w-[50%] items-center justify-center py-10 lg:py-0"
    >
      <img
        src="../assets/images/auth/01.png"
        alt=""
        class="w-[65%] lg:w-[40%]"
      />
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, nextTick } from "vue";
import { API_BASE_URL, GETOTP, CHECKOTP, RESETPASS } from "../utils/appConst";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const email = ref("");
const password = ref("");
const confirmpassword = ref("");
const emailNotRegistered = ref(false);
const serverError = ref(false);
const resetOtpSent = ref(false);
const otpNotCorrect = ref(false);
const otpCorrect = ref(false);
const passwordNotMach = ref(false);
const viewPassword = ref(false);
const passwordShort = ref(false);
const invalidResetToken = ref(false);
const passReseted = ref(false);
const resendDisabled = ref(true);
const isLoading = ref(false);
const resetToken = ref("");
const countdown = ref(60);

const otpCode = ref(["", "", "", "", "", ""]);
const getOtpUrl = `${API_BASE_URL}${GETOTP}`;
const checkOtpUrl = `${API_BASE_URL}${CHECKOTP}`;
const resetPassUrl = `${API_BASE_URL}${RESETPASS}`;
const { t, locale } = useI18n();

onMounted(() => {
  inputListener();
});

const togglePassword = () => {
  viewPassword.value = !viewPassword.value;
};

const requestOtp = async () => {
  try {
    emailNotRegistered.value = false;
    serverError.value = false;
    resendDisabled.value = true;
    isLoading.value = true;
    if (countdown.value != 60) {
      countdown.value = 60;
    }
    const response = await fetch(getOtpUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ userEmail: email.value }),
    });
    const result = await response.json();
    console.log(result);

    if (result.error) {
      if (result.error == "User not found") {
        emailNotRegistered.value = true;
        toast(
          t(
            "this-email-address-was-not-found-please-double-check-your-email-or-sign-up-for-a-new-account"
          ),
          {
            autoClose: 2500,
            type: "error",
            theme: "light",
          }
        );
      } else if (
        result.error ==
        "Too many requests. Please wait before requesting another OTP."
      ) {
        serverError.value = true;
        toast(result.error, {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
      } else {
        serverError.value = true;
        toast(t('were-fixing-a-technical-issue-so-please-try'), {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
      }
      isLoading.value = false;
      return;
    }

    if (result.message) {
      if (result.message == "Password reset OTP sent successfully") {
        resetOtpSent.value = true;
        var intervalId = setInterval(() => {
          decrementCountdown(intervalId);
        }, 800);
      }
    }
    isLoading.value = false;
    await nextTick();
    inputListener();
  } catch (error) {
    console.error("Authentication failed:", error);
    isLoading.value = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const verifyOtp = async () => {
  try {
    otpCorrect.value = false;
    serverError.value = false;
    resetToken.value = "";
    isLoading.value = true;
    const response = await fetch(checkOtpUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        userEmail: email.value,
        resetOtp: otpCode.value.join(""),
      }),
    });
    const result = await response.json();

    if (result.error) {
      if (result.error == "Invalid OTP") {
        otpNotCorrect.value = true;
        toast(t("otp-code-is-not-correct"), {
          autoClose: 2500,
          type: "error",
          theme: "light",
        });
      } else {
        serverError.value = true;
      }
      isLoading.value = false;
      return;
    }

    if (result.message == "Success") {
      resetToken.value = result.token;
      otpCorrect.value = true;
    }
    isLoading.value = false;
  } catch (error) {
    console.error("Authentication failed:", error);
    isLoading.value = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const resetPassword = async () => {
  try {
    passwordNotMach.value = false;
    passwordShort.value = false;
    invalidResetToken.value = false;
    serverError.value = false;
    passReseted.value = false;
    if (password.value.length < 6) {
      passwordShort.value = true;
      return;
    }
    if (confirmpassword.value != password.value) {
      passwordNotMach.value = true;
      return;
    }
    const response = await fetch(resetPassUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${resetToken.value}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        newPassword: password.value,
      }),
    });
    const result = await response.json();

    if (result.error) {
      if (
        result.error === "Invalid token" ||
        result.error === "Token has expired"
      ) {
        invalidResetToken.value = true;
      } else {
        serverError.value = true;
      }
      return;
    }

    if (result.message == "Password updated successfully") {
      passReseted.value = true;
    }
  } catch (error) {
    console.error("Authentication failed:", error);
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const userHiddenMail = () => {
  return email.value.substring(0, 2) + "****@" + email.value.split("@")[1];
};

const decrementCountdown = (intervalId: any) => {
  countdown.value = countdown.value - 0.5;
  if (countdown.value === 0) {
    resendDisabled.value = false;
    clearInterval(intervalId);
  }
};

const inputListener = () => {
  const inputs = document.querySelectorAll(".otp-input");
  console.log(inputs.length);

  for (let i = 0; i < inputs.length; i++) {
    console.log("added listenr");

    inputs[i].addEventListener("keydown", function (event: any) {
      if (event.key === "Backspace") {
        otpCode.value[i] = "";
        if (i !== 0) (inputs[i - 1] as HTMLElement).focus();
      } else {
        if (i === inputs.length - 1 && otpCode.value[i] !== "") {
          return true;
        } else if (event.keyCode > 47 && event.keyCode < 58) {
          otpCode.value[i] = event.key;
          if (i !== inputs.length - 1) (inputs[i + 1] as HTMLElement).focus();
          event.preventDefault();
        } else if (event.keyCode > 64 && event.keyCode < 91) {
          otpCode.value[i] = String.fromCharCode(event.keyCode);
          if (i !== inputs.length - 1) (inputs[i + 1] as HTMLElement).focus();
          event.preventDefault();
        }
      }
    });
  }
};
</script>
